/**
 * The AI Digest's structured sections — what improved, what needs attention,
 * opportunities and recommended actions — built from the same KPI figures the
 * rest of the app reads. `alerts` and `recommendations` are passed in already
 * filtered by the caller's session state (acknowledge/dismiss), rather than
 * built fresh here, so a dismissed alert or recommendation can never resurface
 * in the digest. The one-paragraph summary (`buildDigest`,
 * `../generators/digest.ts`) is unchanged; this is additive.
 */

import type { Alert, Digest, DigestAction, DigestPoint, Recommendation } from '@/api/types'
import { formatChange, changeTone, percentChange } from '@/design/format'
import type { GlobalFilters } from '@/lib/filters'

import type { DemoDataset } from '../generators/engine'
import { buildKpis, buildTopCompetitions } from '../generators/overview'

const MAX_ITEMS = 3

export function buildDigestSections(
  dataset: DemoDataset,
  filters: GlobalFilters,
  alerts: readonly Alert[],
  recommendations: readonly Recommendation[],
): Pick<Digest, 'whatImproved' | 'whatNeedsAttention' | 'opportunities' | 'recommendedActions'> {
  const kpis = buildKpis(dataset, filters)
  const topCompetitions = buildTopCompetitions(dataset, filters, 1).data
  const compareLabel = kpis.period?.compareLabel

  const whatImproved: DigestPoint[] = kpis.data
    .filter((kpi) => kpi.previous !== null)
    .map((kpi) => ({ kpi, change: percentChange(kpi.value, kpi.previous) }))
    .filter((entry): entry is { kpi: (typeof kpis.data)[number]; change: number } => entry.change !== null)
    .filter((entry) => changeTone(entry.change, entry.kpi.polarity) === 'positive')
    .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
    .slice(0, MAX_ITEMS)
    .map((entry) => ({
      text: `${entry.kpi.label} ${formatChange(entry.change)}${compareLabel ? ` ${compareLabel}` : ''}`,
      drillDown: entry.kpi.drillDown,
    }))

  const whatNeedsAttention: DigestPoint[] = alerts.slice(0, MAX_ITEMS).map((alert) => ({
    text: `${alert.title} — ${alert.subject}`,
    drillDown: alert.drillDown,
  }))

  const opportunities: DigestPoint[] = recommendations.slice(0, MAX_ITEMS).map((recommendation) => ({
    text: `${recommendation.title}: ${recommendation.estimatedImpact}`,
    drillDown: recommendation.action.drillDown,
  }))

  const recommendedActions: DigestAction[] = [
    alerts[0] ? { label: 'View issue', drillDown: alerts[0].drillDown } : null,
    { label: 'Create audience', drillDown: { path: '/marketing/audiences' } },
    { label: 'Create campaign', drillDown: { path: '/marketing/campaigns/new' } },
    topCompetitions[0] ? { label: 'Open competition', drillDown: topCompetitions[0].drillDown } : null,
    { label: 'Create automation', drillDown: { path: '/automation/workflows' } },
  ].filter((action): action is DigestAction => action !== null)

  return { whatImproved, whatNeedsAttention, opportunities, recommendedActions }
}
